import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** Ловит ошибки рендера и загрузки lazy-чанков, показывает сообщение, «Повторить» и «Скопировать ошибку». */
export default class AdminErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[Admin] ErrorBoundary caught:', error, info.componentStack);
  }

  retry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  copyError = (): void => {
    const { error } = this.state;
    const text = error ? `${error.message}${error.stack ? `\n${error.stack}` : ''}` : '';
    if (!text) return;
    try {
      void navigator.clipboard.writeText(text);
      // Краткая обратная связь — в Mini App нет toast, пользователь может вставить в заметки
      const btn = document.querySelector('[data-admin-copy-btn]');
      if (btn && btn instanceof HTMLButtonElement) {
        const orig = btn.textContent;
        btn.textContent = 'Скопировано';
        setTimeout(() => { btn.textContent = orig; }, 1500);
      }
    } catch {
      // clipboard недоступен
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const errMsg = this.state.error?.message ?? '';
      return (
        <div
          className="admin-page"
          style={{
            padding: '24px',
            textAlign: 'center',
            color: 'var(--text-primary)',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
          }}
        >
          <p style={{ fontSize: '16px', fontWeight: 600 }}>Что-то пошло не так</p>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '320px' }}>
            Не удалось загрузить страницу. Проверьте интернет и попробуйте ещё раз.
          </p>
          {errMsg ? (
            <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', wordBreak: 'break-word', maxWidth: '320px' }}>
              {errMsg}
            </p>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={this.retry}
              className="admin-btn"
              style={{ padding: '12px 24px', fontSize: '15px' }}
            >
              Повторить
            </button>
            {errMsg ? (
              <button
                type="button"
                onClick={this.copyError}
                data-admin-copy-btn
                className="admin-btn admin-btn-secondary"
                style={{ padding: '12px 24px', fontSize: '15px' }}
              >
                Скопировать ошибку
              </button>
            ) : null}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
